export const PERFORMANCE_PAGE = 'performance';

export const performanceContext = (kind, widgetId) => ({ type: 'performance', value: `${kind}:${widgetId}` });

export function parsePerformanceContext(value) {
    const [kind, widgetId] = String(value || '').split(':');
    return { kind: kind === 'courses' ? 'courses' : 'sims', widgetId: widgetId || null };
}

const NEUTRAL = 'var(--text)';
const RED = 'var(--red)';
const GREEN = 'var(--green)';
const AMBER = 'var(--amber)';
const BLUE = '#3B82F6';
export const DEEP_RED = '#B71C1C';

export const PERFORMANCE = {
    sims: {
        label: 'Simulations',
        metric: 'simulations',
        aggregation: 'sim_performance',
        rowsKey: 'simPerformance',
        defaultSort: { field: 'failRate', dir: 'desc' },
        storageKey: 'rbx-perf-hidden-cols-sims',
        widgets: [
            { id: 'metric-sends', label: 'Total Sends', color: NEUTRAL, cols: ['sent'], sort: 'sent', dir: 'desc', tableTitle: 'Users by simulations sent' },
            { id: 'metric-click-rate', label: 'Failure Rate', color: RED, cols: ['clicks', 'failRate'], sort: 'failRate', dir: 'desc', tableTitle: 'Users driving failure rate' },
            { id: 'metric-second-stage', label: '2nd Stage Rate', color: RED, cols: ['secondStage', 'secondStageRate'], sort: 'secondStageRate', dir: 'desc', tableTitle: 'Users reaching 2nd stage' },
            { id: 'metric-repeat-clickers', label: 'Repeat Clickers', color: AMBER, cols: ['clickStreak'], sort: 'clickStreak', dir: 'desc', tableTitle: 'Repeat clickers', rowHighlight: (r) => r.clickStreak >= 2 },
            { id: 'metric-repeat-offenders', label: 'Repeat Offenders', chipLabel: 'Repeat Offenders · Clicks > 1', color: DEEP_RED, cols: ['clicks'], sort: 'clicks', dir: 'desc', tableTitle: 'Repeat offenders', minCount: 2, filterNote: 'only users with more than 1 click are shown' },
            { id: 'metric-report-rate', label: 'Report Rate', color: GREEN, cols: ['reported', 'reportRate'], sort: 'reportRate', dir: 'desc', tableTitle: 'Users reporting simulations' },
            { id: 'metric-flagged', label: 'Flagged Sims', color: AMBER, page: true },
        ],
        columns: [
            { id: 'email', label: 'Email', type: 'string' },
            { id: 'sent', label: 'Sent', type: 'number' },
            { id: 'clicks', label: 'Clicks', type: 'number' },
            { id: 'failRate', label: 'Failure rate', type: 'pct' },
            { id: 'secondStage', label: '2nd stage clicks', type: 'number' },
            { id: 'secondStageRate', label: '2nd stage rate', type: 'pct' },
            { id: 'clickStreak', label: 'Click streak', type: 'number' },
            { id: 'reported', label: 'Reported', type: 'number' },
            { id: 'reportRate', label: 'Report rate', type: 'pct' },
        ],
    },
    courses: {
        label: 'Courses',
        metric: 'courses',
        aggregation: 'course_performance',
        rowsKey: 'coursePerformance',
        defaultSort: { field: 'trainingDebt', dir: 'desc' },
        storageKey: 'rbx-perf-hidden-cols-courses',
        widgets: [
            { id: 'metric-courses-sent', label: 'Courses Sent', color: NEUTRAL, cols: ['sent'], sort: 'sent', dir: 'desc', tableTitle: 'Users by courses sent' },
            { id: 'metric-courses-completed', label: 'Courses Completed', color: GREEN, cols: ['sent', 'completed'], sort: 'completed', dir: 'desc', tableTitle: 'Users by courses completed' },
            { id: 'metric-completion-rate', label: 'Completion Rate', color: GREEN, cols: ['sent', 'completed', 'completeRate'], sort: 'completeRate', dir: 'asc', tableTitle: 'Users by completion rate' },
            { id: 'metric-avg-score', label: 'Avg Score', color: BLUE, cols: ['avgScore'], sort: 'avgScore', dir: 'asc', tableTitle: 'Users by average score' },
            { id: 'metric-pass-mark-rate', label: 'Pass Rate', color: GREEN, cols: ['passed', 'passRate'], sort: 'passRate', dir: 'asc', tableTitle: 'Users by pass rate' },
            { id: 'metric-pass-rate', label: 'Avg Pass Score', color: BLUE, cols: ['avgPassScore'], sort: 'avgPassScore', dir: 'asc', tableTitle: 'Users by average pass score' },
            { id: 'metric-training-debt', label: 'Training Debt', color: RED, cols: ['trainingDebt', 'reminders'], sort: 'trainingDebt', dir: 'desc', tableTitle: 'Users carrying training debt', rowHighlight: (r) => r.trainingDebt > 0 },
            { id: 'metric-course-lag', label: 'Avg Course Lag', color: AMBER, cols: ['courseLag'], sort: 'courseLag', dir: 'desc', tableTitle: 'Users by course lag', rowHighlight: (r) => r.courseLag >= 30 },
        ],
        columns: [
            { id: 'email', label: 'Email', type: 'string' },
            { id: 'sent', label: 'Sent', type: 'number' },
            { id: 'completed', label: 'Complete', type: 'number' },
            { id: 'completeRate', label: 'Complete %', type: 'pct' },
            { id: 'passed', label: 'Passed', type: 'number' },
            { id: 'passRate', label: 'Passed %', type: 'pct' },
            { id: 'avgScore', label: 'Avg score', type: 'pct' },
            { id: 'avgPassScore', label: 'Avg pass score', type: 'pct' },
            { id: 'reminders', label: 'Course reminders', type: 'number' },
            { id: 'trainingDebt', label: 'Training debt', type: 'pct' },
            { id: 'courseLag', label: 'Avg course lag', type: 'lag' },
        ],
    },
};

export function formatPerformanceCell(column, value) {
    if (value == null) return null;
    if (column.type === 'pct') return `${Math.round(value)}%`;
    if (column.type === 'lag') return value >= 30 ? '30d+' : `${Number(value).toFixed(1)}d`;
    if (column.type === 'number') return Number(value).toLocaleString();
    return value;
}

export function loadHiddenColumns(storageKey) {
    try {
        const stored = JSON.parse(localStorage.getItem(storageKey));
        return Array.isArray(stored) ? stored : [];
    } catch {
        return [];
    }
}

export function saveHiddenColumns(storageKey, hidden) {
    try { localStorage.setItem(storageKey, JSON.stringify(hidden)); } catch { /* ignore */ }
}
